import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_CONTROLROOMS_AUTH_API || 'https://api.dev.controlrooms.ai';

class AuthorizationService {
  constructor() {
    this.user = null;
    this.userTenants = [];
    this.accessiblePlants = [];
    this.isInitialized = false;
  }

  /**
   * Initialize the authorization service by calling ControlRooms accounts service
   * @param {string} token - JWT token from Auth0
   * @returns {Promise<boolean>} - True if user is authorized, false otherwise
   */
  async initialize(token) {
    try {
      // First, get user information from the JWT token
      const tokenData = this.parseJWT(token);
      
      if (!tokenData) {
        throw new Error('Failed to parse JWT token');
      }

      // Extract basic user information from token
      const tokenUserInfo = tokenData['https://controlrooms.ai/user'];
      if (!tokenUserInfo) {
        throw new Error('JWT token missing ControlRooms user information');
      }

      // Call the ControlRooms accounts service to get user info and tenant roles
      
      let userResponse;
      try {
        userResponse = await axios.get(`${API_BASE_URL}/accounts-v2/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } catch (axiosError) {
        console.error('[Authorization] User API error details:', {
          message: axiosError.message,
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          data: axiosError.response?.data,
          headers: axiosError.response?.headers
        });
        throw axiosError;
      }
      
      // Extract user info and tenant roles from the response
      const userInfo = userResponse.data || {};
      const tenantRoles = userInfo.tenant_roles || [];

      // Transform tenant_roles to the format expected by the rest of the service
      this.userTenants = tenantRoles.map(tenantRole => {
        return {
          id: tenantRole.tenant.tenant_id,
          name: tenantRole.tenant.tenant_name,
          roles: tenantRole.roles.map(role => role.role_desc)
        };
      });
      
      // Update user object with full info from accounts service
      this.user = {
        id: userInfo.user_id,
        uuid: userInfo.user_uuid,
        roles: userInfo.admin_roles?.map(role => role.role_desc) || []
      };
      
      if (!this.userTenants || this.userTenants.length === 0) {
        throw new Error('User has no authorized tenants');
      }

      // Check if user has OBSERVER role in any tenant
      const hasObserverRole = this.userTenants.some(tenant =>
        tenant.roles && tenant.roles.includes('OBSERVER')
      );

      if (!hasObserverRole) {
        throw new Error('User does not have OBSERVER role in any tenant');
      }

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('[Authorization] Authorization initialization failed:', error);
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Parse JWT token to extract payload
   * @param {string} token - JWT token
   * @returns {Object|null} - Token payload or null if invalid
   */
  parseJWT(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to parse JWT token:', error);
      return null;
    }
  }

  /**
   * Get plants that the user has access to based on their tenant roles
   * @param {Array} allPlants - All available plants from the backend
   * @returns {Array} - Filtered list of plants user can access
   */
  getAccessiblePlants(allPlants) {
    if (!this.isInitialized || !allPlants) {
      return [];
    }

    // Filter plants based on tenant descriptions
    const accessiblePlants = allPlants.filter(plant => {
      // Check if any of the user's tenants match the plant description
      const hasAccess = this.userTenants.some(tenant => {
        return tenant.name === plant.description;
      });

      return hasAccess;
    });

    return accessiblePlants;
  }

  /**
   * Check if user has access to a specific plant
   * @param {Object} plant - Plant object to check access for
   * @returns {boolean} - True if user has access, false otherwise
   */
  hasPlantAccess(plant) {
    if (!this.isInitialized || !plant) {
      return false;
    }

    const hasAccess = this.userTenants.some(tenant =>
      tenant.name === plant.description
    );

    return hasAccess;
  }

  /**
   * Get the tenant that corresponds to a specific plant
   * @param {Object} plant - Plant object
   * @returns {Object|null} - Tenant object or null if no match
   */
  getPlantTenant(plant) {
    if (!this.isInitialized || !plant) {
      return null;
    }

    return this.userTenants.find(tenant => 
      tenant.name === plant.description
    ) || null;
  }

  /**
   * Get user's primary tenant (first tenant with OBSERVER role)
   * @returns {Object|null} - Primary tenant or null
   */
  getPrimaryTenant() {
    if (!this.isInitialized) {
      return null;
    }

    return this.userTenants.find(tenant =>
      tenant.roles && tenant.roles.includes('OBSERVER')
    ) || null;
  }

  /**
   * Check if user is authorized
   * @returns {boolean} - True if user is authorized, false otherwise
   */
  isAuthorized() {
    return this.isInitialized && this.user !== null;
  }

  /**
   * Get user information
   * @returns {Object|null} - User object or null
   */
  getUser() {
    return this.user;
  }

  /**
   * Get user's tenants
   * @returns {Array} - Array of tenant objects
   */
  getUserTenants() {
    return this.userTenants;
  }

  /**
   * Get authorization headers for API calls
   * @param {string} token - JWT token
   * @returns {Object} - Headers object with authorization
   */
  getAuthHeaders(token) {
    if (!this.isInitialized) {
      throw new Error('Authorization service not initialized');
    }

    const primaryTenant = this.getPrimaryTenant();
    if (!primaryTenant) {
      throw new Error('No primary tenant found');
    }

    return {
      Authorization: `Bearer ${token}`,
      'X-User-ID': this.user.id.toString(),
      'X-Tenant-ID': primaryTenant.id.toString(),
      'X-User-Roles': primaryTenant.roles.join(',')
    };
  }

  /**
   * Reset the authorization service state
   */
  reset() {
    this.user = null;
    this.userTenants = [];
    this.accessiblePlants = [];
    this.isInitialized = false;
  }
}

// Export a singleton instance
const authorizationService = new AuthorizationService();
export default authorizationService;
